import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as moment from "moment";
import axios from "axios";
import {
  TABLE_COMPANY_LIST,
  PAGE_SIZE,
  FILTER_TYPE_BUTTON,
  TABLE_COMPANY_LIST_ENDPOINT,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import { companyTypes } from "@utils/filterOptions";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import { loadAllCompanies } from "./CompanyList.actionCreators";
import { setAllCompaniesLoadingState } from "./CompanyList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import CompanyId from "./components/CompanyId/CompanyId";
import CompanyInfo from "./components/CompanyInfo/CompanyInfo";
import CompanyType from "./components/CompanyType/CompanyType";
import CompanyAddress from "./components/CompanyAddress/CompanyAddress";
import { columnHeader, isEmpty } from "@utils/utils";
import UserInfo from "@utils/UserInfo";

const PrepareDataTable = (props) => {
  const { t, isRefreshList, updateRefeshStatus, columnList } = props;

  const [data, setData] = useState([]);
  const [activeStatus, setActiveStatus] = useState(1);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState([]);
  const mainCountryInfo = UserInfo.countryInfo();

  const countryId = UserInfo.countryId();
  const countryList = UserInfo.countryListForFilter(t);

  const [selectedFilter, setSelectedFilter] = useState([
    {
      column: "country",
      value: {
        value: mainCountryInfo?.country?.id,
        label: t(mainCountryInfo?.country?.name),
      },
    },
  ]);
  const dataURL = TABLE_COMPANY_LIST_ENDPOINT;

  const dispatch = useDispatch();

  let columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "id",
      Cell: ({ cell: { value } }) => (
        <CompanyId
          data={"#" + value.id}
          id={value.id}
          isActive={value.isActive}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: false,
    },
    {
      Header: t("COMPANY_NAME"),
      title: "COMPANY_NAME",
      accessor: "name",
      Cell: ({ cell: { value } }) => (
        <CompanyId
          data={value.name}
          id={value.id}
          isActive={value.isActive}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: false,
    },
    {
      Header: t("COMPANY_TYPE"),
      title: "COMPANY_TYPE",
      accessor: "type",
      Cell: ({ cell: { value } }) => (
        <CompanyType
          isCentre={value.isCentre}
          isClient={value.isClient}
          isHaulier={value.isHaulier}
          isSupplier={value.isSupplier}
          isActive={value.isActive}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: false,
    },
    {
      Header: t("COMPANY_IDENTICATION_NUMBER"),
      title: "COMPANY_IDENTICATION_NUMBER",
      accessor: "identificationNumber",
      Cell: ({ cell: { value } }) => (
        <CompanyInfo
          data={value.identificationNumber}
          id={value.id}
          isActive={value.isActive}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: false,
    },
    {
      Header: t("COMPANY_ADDRESS"),
      title: "COMPANY_ADDRESS",
      accessor: "address",
      Cell: ({ cell: { value } }) => (
        <CompanyAddress
          data={value.address}
          id={value.id}
          isActive={value.isActive}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  const companyListByIds = useSelector((state) => state.companyList.byId);
  const companyListAllIds = useSelector((state) => state.companyList.allIds);
  const filteredRecords = useSelector(
    (state) => state.companyList.filteredRecords
  );

  const isCompanyListLoading = useSelector(
    (state) => state.companyList.isLoading
  );

  useEffect(() => {
    setLoading(isCompanyListLoading);
  }, [isCompanyListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });

    let salesman = [];
    let companyTypesArray = [];
    let selectedCountries = [];
    if (additionalFilters.length > 0) {
      const salesmanFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "salesman"
      );
      salesman = salesmanFilter?.map((item) => item.value);
      const companyTypeFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "companyType"
      );
      companyTypesArray = companyTypeFilter?.map((item) => item.value);

      const countryTypeFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "country"
      );
      selectedCountries = countryTypeFilter?.map((item) => item.value);
    }
    if (selectedCountries.length === 0) {
      selectedCountries = countryId;
    }
    // Set the loading state
    setLoading(true);

    dispatch(setAllCompaniesLoadingState());
    dispatch(
      loadAllCompanies({
        dataURL,
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        status: activeStatus,
        salesman,
        companyTypesArray,
        selectedCountries,
      })
    );
  };

  useEffect(() => {
    if (companyListAllIds.length > 0) {
      const responseData = companyListAllIds.map((id) => {
        const tempData = { ...companyListByIds[id] };
        tempData.name = {
          id: tempData.id,
          name: tempData.name,
          isActive: tempData.isActive,
        };
        tempData.type = {
          isCentre: tempData?.isCentre,
          isClient: tempData?.isClient,
          isHaulier: tempData?.isHaulier,
          isSupplier: tempData?.isSupplier,
          isActive: tempData.isActive,
        };
        tempData.identificationNumber = {
          id: tempData.id,
          identificationNumber: tempData.identificationNumber,
          isActive: tempData.isActive,
        };
        tempData.address = {
          id: tempData.id,
          address: tempData.shippingAddress,
          isActive: tempData.isActive,
        };
        tempData.id = {
          id: tempData.id,
          isActive: tempData.isActive,
        };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, companyListAllIds, companyListByIds, filteredRecords]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        appliedFilters: additionalFilters,
      });
    }
  }, [activeStatus, additionalFilters]);

  const changeStatus = (label) => {
    let statusNew = activeStatus === 1 ? 2 : 1;
    setActiveStatus(statusNew);
  };

  return (
    <EncloseTable
      type={TABLE_COMPANY_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={PAGE_SIZE}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      currentPageToFirst={currentPageToFirst}
      setCurrentPageToFirst={setCurrentPageToFirst}
      loading={loading}
      setAdditionalFilters={setAdditionalFilters}
      additionalFilters={additionalFilters}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      selectedFilter={selectedFilter}
      extraFilters={[
        ...(countryList?.length > 1
          ? [
              {
                type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
                label: t("COUNTRY"),
                name: "country",
                labelClass: "company-list__admins-list-label",
                containerClass: "company-list__admins-list-dropdown-container",
                options: countryList,
                placeholder: t("SEARCH"),
                defaultOption: "Nothing Selected",
              },
            ]
          : []),
        {
          type: FILTER_TYPE_BUTTON,
          label: "COMPANIES_LIST_TOGGLE_ACTIVE_ONLY",
          name: "admins",
          labelClass: "company-list__admins-list-label",
          containerClass: "company-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status: activeStatus,
          changeStatus: { changeStatus },
        },
        {
          type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
          label: t("COMPANIES_FILTER_COMPANY_TYPE"),
          name: "companyType",
          labelClass: "company-list__admins-list-label",
          containerClass: "company-list__admins-list-dropdown-container",
          defaultOption: "Nothing Selected",
          options: companyTypes(t),
          placeholder: t("SEARCH"),
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
};

export default withTranslation()(PrepareDataTable);
